import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Stack from "@mui/material/Stack";
import Divider from "@mui/material/Divider";
import Chip from "@mui/material/Chip";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import SpeedIcon from "@mui/icons-material/Speed";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import AddIcon from "@mui/icons-material/Add";
import ThumbUpOutlinedIcon from "@mui/icons-material/ThumbUpOutlined";
import ThumbDownOutlinedIcon from "@mui/icons-material/ThumbDownOutlined";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import SendIcon from "@mui/icons-material/Send";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import TuneIcon from "@mui/icons-material/Tune";
import BuildIcon from "@mui/icons-material/Build";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

interface FaqItem {
  question: string;
  answer: React.ReactNode;
  category: string;
  anchor?: string;
}

const faqItems: FaqItem[] = [
  // --- Performance ---
  {
    category: "Performance",
    question: "Why is my first question slower than the rest?",
    answer: (
      <>
        <Typography variant="body2" gutterBottom>
          The system uses a <strong>serverless database</strong> that automatically pauses when no one has used it for a while. When you send your first question, the database needs to "wake up," which can take <strong>a few extra seconds</strong>.
        </Typography>
        <Typography variant="body2">
          After that, the database stays active and subsequent questions are processed much faster. This is completely normal and helps keep operating costs low.
        </Typography>
      </>
    ),
  },
  {
    category: "Performance",
    question: "The episode filter seems empty when I open it. Is it broken?",
    answer: (
      <>
        <Typography variant="body2" gutterBottom>
          No, it's not broken! The episode list is loaded from the server when you first open the page. If the server is waking up (see above), this can take a moment.
        </Typography>
        <Typography variant="body2">
          <strong>Just wait a few seconds</strong> and the episodes will appear. You can also start typing in the search box — results will show up once the data has loaded.
        </Typography>
      </>
    ),
  },
  // --- Sample Questions ---
  {
    category: "Sample Questions",
    question: "How do I use the sample questions on the welcome screen?",
    answer: (
      <>
        <Typography variant="body2" gutterBottom>
          When the chat is empty, you'll see a grid of sample questions. You can interact with them in two ways:
        </Typography>
        <Box component="ul" sx={{ pl: 2, mb: 1 }}>
          <li>
            <Typography variant="body2">
              <strong>Click the question text (card body)</strong> — sends the question immediately to the chatbot.
            </Typography>
          </li>
          <li>
            <Typography variant="body2">
              <strong>Click the copy icon</strong> at the right of the card — copies the question into the input box below so you can <strong>edit or adapt it</strong> before sending.
            </Typography>
          </li>
        </Box>
        <Typography variant="body2">
          Use the copy icon when you want to tweak a sample — for example, changing a topic, narrowing the scope, or asking in another language.
        </Typography>
      </>
    ),
  },
  // --- Filters ---
  {
    category: "Filters",
    question: "What can I search with the episode filter?",
    answer: (
      <>
        <Typography variant="body2" gutterBottom>
          The episode filter is a powerful search tool. You can search by:
        </Typography>
        <Box component="ul" sx={{ pl: 2, mb: 1 }}>
          <li><Typography variant="body2"><strong>Episode title</strong> — e.g., "Virtual Power Plants"</Typography></li>
          <li><Typography variant="body2"><strong>Guest name</strong> — e.g., "Mohua Mukherjee"</Typography></li>
          <li><Typography variant="body2"><strong>Topics</strong> — e.g., "hydrogen," "solar," "nuclear"</Typography></li>
          <li><Typography variant="body2"><strong>Named entities</strong> — companies, organizations, technologies</Typography></li>
          <li><Typography variant="body2"><strong>Mini-series name</strong> — e.g., "Climate Science"</Typography></li>
          <li><Typography variant="body2"><strong>Description keywords</strong></Typography></li>
        </Box>
        <Typography variant="body2">
          You can combine multiple search terms separated by spaces — all terms must match. You can also select <strong>multiple episodes</strong> to narrow down answers to just those episodes.
        </Typography>
      </>
    ),
  },
  {
    category: "Filters",
    question: "What happens when I select episodes in the filter?",
    answer: (
      <>
        <Typography variant="body2" gutterBottom>
          When you select one or more episodes, the chatbot will <strong>only search within those episodes</strong> to answer your questions. This is useful when you want specific information from a known episode.
        </Typography>
        <Typography variant="body2">
          Selected episodes appear as <strong>blue chips</strong> above the input box. You can remove them by clicking the "x" on each chip. When no episodes are selected, the bot searches across all available content.
        </Typography>
      </>
    ),
  },
  // --- Input Bar Buttons ---
  {
    category: "Input Bar",
    question: "What does the \"+\" button (next to the text input) do?",
    answer: (
      <>
        <Typography variant="body2" gutterBottom>
          The <strong>+ button</strong> opens a quick <strong>Chat Settings</strong> panel with two options:
        </Typography>
        <Box component="ul" sx={{ pl: 2, mb: 1 }}>
          <li>
            <Typography variant="body2">
              <strong>Exclude reference links</strong> — When enabled, external reference links are left out of the answer sources. Show material (transcripts, news segments, FAQ, and episode descriptions) is always used either way.
            </Typography>
          </li>
          <li>
            <Typography variant="body2">
              <strong>Memory</strong> — Controls how many previous questions the bot remembers in the current conversation (0–10). A higher number means better follow-up answers, but may slow things down slightly. Default is 2.
            </Typography>
          </li>
        </Box>
      </>
    ),
  },
  {
    category: "Input Bar",
    question: "What does the filter icon (funnel) in the input bar do?",
    answer: (
      <Typography variant="body2">
        The <strong>filter icon</strong> opens the same episode filter described above — it lets you quickly select or search for episodes to narrow down the bot's answers. It's a shortcut so you don't have to open the full sidebar.
      </Typography>
    ),
  },
  // --- Response Buttons ---
  {
    category: "Responses",
    question: "What do the thumbs up / thumbs down buttons under a response do?",
    answer: (
      <>
        <Typography variant="body2" gutterBottom>
          These buttons let you <strong>rate the quality</strong> of the bot's response:
        </Typography>
        <Box component="ul" sx={{ pl: 2, mb: 1 }}>
          <li><Typography variant="body2"><strong>Thumbs up</strong> — The answer was helpful and accurate.</Typography></li>
          <li><Typography variant="body2"><strong>Thumbs down</strong> — The answer was unhelpful or incorrect.</Typography></li>
        </Box>
        <Typography variant="body2">
          After clicking either button, you'll be asked to provide an optional star rating and written feedback. This feedback is invaluable for improving the system. You can only rate each response once.
        </Typography>
      </>
    ),
  },
  {
    category: "Responses",
    question: "What does the copy button under a response do?",
    answer: (
      <Typography variant="body2">
        The <strong>copy button</strong> copies the full text of the bot's response to your clipboard so you can paste it into emails, documents, or notes.
      </Typography>
    ),
  },
  // --- Behavior & Limits ---
  {
    category: "Behavior",
    anchor: "answer-quality",
    question: "Why does the bot sometimes say \"I don't know\"?",
    answer: (
      <>
        <Typography variant="body2" gutterBottom>
          The bot is built to refuse rather than fabricate. Every answer is grounded in the show's own transcripts, news segments, episode descriptions, FAQ pages, and curated reference links — and <strong>nothing else</strong>. If the source material doesn't cover what you asked, the bot will say so instead of inventing a plausible-sounding answer. When it does have an answer, it points you to the exact transcript timestamp or reference link so you can verify who said it and where.
        </Typography>
        <Typography variant="body2" gutterBottom>
          The bot has <strong>no access to the open internet</strong>, no live web search, and no general world knowledge beyond what the show has discussed.
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 600, mt: 2, mb: 1 }}>
          Common reasons you might see "I don't know":
        </Typography>
        <Box component="ul" sx={{ pl: 2, mb: 1 }}>
          <li><Typography variant="body2"><strong>The topic genuinely isn't covered.</strong> The show is broad but not infinite — niche or very recent topics may not appear in any episode.</Typography></li>
          <li><Typography variant="body2"><strong>Your episode filter is too narrow.</strong> The bot only searches the episodes you've selected. Try clearing the filter chip below the input box.</Typography></li>
          <li><Typography variant="body2"><strong>"Exclude reference links" is on.</strong> Some answers live in linked references rather than transcripts. Open the <strong>+</strong> menu next to the input and turn it off.</Typography></li>
          <li><Typography variant="body2"><strong>Memory from earlier turns is steering the search.</strong> The system rewrites your question using recent history; if it guesses wrong, it can search for the wrong thing. Open the <strong>+</strong> menu and set <strong>Memory</strong> to <strong>0</strong>, or refresh the page to start clean.</Typography></li>
          <li><Typography variant="body2"><strong>Wording mismatch.</strong> The bot matches your question against the show's content by meaning, not exact words. If your phrasing is far from how the show talks about the topic, retrieval can come up empty. Try rewording — or use the show's own vocabulary if you know it (e.g. "VPP" instead of "aggregated distributed energy resources").</Typography></li>
          <li><Typography variant="body2"><strong>You're asking outside the show's scope.</strong> Live news, today's prices, what happened yesterday, anything the show hasn't already discussed — the bot can't fetch it.</Typography></li>
          <li><Typography variant="body2"><strong>You asked for too much at once.</strong> The bot retrieves a handful of relevant passages per question — it can't reconstruct a full episode word-for-word, or summarize every episode in one shot. Ask about a specific episode or a specific theme.</Typography></li>
          <li><Typography variant="body2"><strong>The episode isn't available.</strong> Episode numbers beyond the latest aired episode, numbers that were never used, or episodes that have aired but haven't been loaded into the bot's context yet, will return a "not available" message.</Typography></li>
        </Box>
      </>
    ),
  },
  {
    category: "Behavior",
    anchor: "standard-format",
    question: "Why did my answer come back as a list instead of the format I asked for?",
    answer: (
      <>
        <Typography variant="body2" gutterBottom>
          For some question types, the bot builds the answer in a fixed standard layout regardless of how you phrased the request — for example, topic searches across episodes, episode cross-references, guest-appearance lookups, and mini-series listings.
        </Typography>
        <Typography variant="body2" gutterBottom>
          This is intentional. These answers are assembled directly from indexed metadata rather than rewritten by a language model, which keeps results consistent across questions and prevents the bot from inventing fields (like fake quotes or fake timestamps) just because you asked for them.
        </Typography>
        <Typography variant="body2">
          If you need the same data in a different shape, copy the answer and ask the bot to reformat it as a follow-up — it's free to restructure existing content, just not to fabricate new fields.
        </Typography>
      </>
    ),
  },
  // --- General Usage ---
  {
    category: "General",
    question: "How do I get the best answers from the chatbot?",
    answer: (
      <>
        <Typography variant="body2" gutterBottom>
          Here are some tips for getting great answers:
        </Typography>
        <Box component="ul" sx={{ pl: 2, mb: 1 }}>
          <li><Typography variant="body2"><strong>Be specific</strong> — Instead of "Tell me about energy," try "What are the main challenges for hydrogen adoption discussed in the show?"</Typography></li>
          <li><Typography variant="body2"><strong>Use episode filters</strong> — If you know which episode you're interested in, select it to get more focused answers.</Typography></li>
          <li><Typography variant="body2"><strong>Ask follow-up questions</strong> — The bot remembers your recent conversation, so you can ask "Can you elaborate on that?" or "What about the costs?"</Typography></li>
          <li><Typography variant="body2"><strong>Try the example questions</strong> — When the chat is empty, you'll see suggested questions. These are a great starting point!</Typography></li>
        </Box>
      </>
    ),
  },
  {
    category: "General",
    question: "Can I ask about multiple episodes at once?",
    answer: (
      <Typography variant="body2">
        Yes! You can either select multiple episodes using the filter and ask a comparative question, or simply ask the bot directly — e.g., <em>"Which episodes discuss nuclear energy?"</em>. The bot can search across all episodes when no filter is applied.
      </Typography>
    ),
  },
  {
    category: "General",
    question: "What types of questions can I ask?",
    answer: (
      <>
        <Typography variant="body2" gutterBottom>
          You can ask a wide variety of questions, including:
        </Typography>
        <Box component="ul" sx={{ pl: 2, mb: 1 }}>
          <li><Typography variant="body2">Episode summaries and key takeaways</Typography></li>
          <li><Typography variant="body2">Specific topics discussed (e.g., "What was said about carbon capture?")</Typography></li>
          <li><Typography variant="body2">Guest information and their viewpoints</Typography></li>
          <li><Typography variant="body2">Comparisons across episodes</Typography></li>
          <li><Typography variant="body2">Lists of tags, acronyms, or referenced entities</Typography></li>
          <li><Typography variant="body2">Conflicting opinions within episodes</Typography></li>
          <li><Typography variant="body2">Which episodes cover a specific topic</Typography></li>
        </Box>
      </>
    ),
  },
  {
    category: "General",
    question: "Can I ask questions in other languages?",
    answer: (
      <>
        <Typography variant="body2" gutterBottom>
          Yes — the chatbot understands and responds in many languages including German, French, Spanish, Italian, Portuguese, Dutch, Japanese, Korean, Chinese, Russian, Polish, Swedish, and more. It will reply in the same language you use.
        </Typography>
        <Typography variant="body2" gutterBottom>
          However, keep in mind that <strong>all episode content is in English</strong>. The system works best when your question is in English, because the search engine matches your question against English transcripts. When you ask in another language, the bot will still find relevant content and translate the answer for you, but the search accuracy may be slightly lower.
        </Typography>
        <Typography variant="body2">
          <strong>Tip:</strong> For the most accurate results, try asking in English. If you prefer another language, the bot will do its best to deliver a helpful answer — it just might occasionally miss some relevant content.
        </Typography>
      </>
    ),
  },
  {
    category: "General",
    question: "Why did I get a \"daily request limit\" error?",
    answer: (
      <Typography variant="body2">
        Each user has a daily limit on the number of questions they can ask. This prevents abuse and keeps the system fair for everyone. The limit resets every day. If you need a higher limit, please contact your administrator.
      </Typography>
    ),
  },
  {
    category: "General",
    question: "My question is taking a long time. What should I do?",
    answer: (
      <>
        <Typography variant="body2" gutterBottom>
          Complex questions (especially those spanning many episodes or requiring detailed analysis) can take longer — typically <strong>10–30 seconds</strong>. If it's your very first question of the session, add a few extra seconds for the database to wake up.
        </Typography>
        <Typography variant="body2">
          If a question takes more than a minute, there may be a connectivity issue. Try refreshing the page and asking again.
        </Typography>
      </>
    ),
  },
];

const categoryIcons: Record<string, React.ReactNode> = {
  Performance: <SpeedIcon fontSize="small" />,
  "Sample Questions": <ContentCopyIcon fontSize="small" />,
  Filters: <FilterAltOutlinedIcon fontSize="small" />,
  "Input Bar": <TuneIcon fontSize="small" />,
  Responses: <ChatBubbleOutlineIcon fontSize="small" />,
  Behavior: <InfoOutlinedIcon fontSize="small" />,
  General: <HelpOutlineIcon fontSize="small" />,
};

const categoryColorsLight: Record<string, string> = {
  Performance: "#e3f2fd",
  "Sample Questions": "#e0f7fa",
  Filters: "#f3e5f5",
  "Input Bar": "#fff3e0",
  Responses: "#e8f5e9",
  Behavior: "#ede7f6",
  General: "#fce4ec",
};

const categoryColorsDark: Record<string, string> = {
  Performance: "rgba(33, 150, 243, 0.15)",
  "Sample Questions": "rgba(0, 188, 212, 0.15)",
  Filters: "rgba(156, 39, 176, 0.15)",
  "Input Bar": "rgba(255, 152, 0, 0.15)",
  Responses: "rgba(76, 175, 80, 0.15)",
  Behavior: "rgba(103, 58, 183, 0.15)",
  General: "rgba(233, 30, 99, 0.15)",
};

function FaqPage() {
  const theme = useTheme();
  const location = useLocation();
  const isDark = theme.palette.mode === "dark";
  const categoryColors = isDark ? categoryColorsDark : categoryColorsLight;
  const [expandedPanel, setExpandedPanel] = useState<string | false>(false);

  const handleAccordionChange = (panel: string) => (_: React.SyntheticEvent, isExpanded: boolean) => {
    setExpandedPanel(isExpanded ? panel : false);
  };

  const categories = Array.from(new Set(faqItems.map((item) => item.category)));

  useEffect(() => {
    const hash = location.hash.replace(/^#/, "");
    if (!hash) return;
    const target = faqItems.find((item) => item.anchor === hash);
    if (!target) return;
    const panelId = target.anchor!;
    setExpandedPanel(panelId);
    const el = document.getElementById(panelId);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [location.hash]);

  return (
    <Box sx={{ height: "100%", overflowY: "auto", pb: 8 }}>
      <Box sx={{ maxWidth: "860px", margin: "0 auto", p: { xs: 2, md: 4 } }}>
        {/* Header */}
        <Stack spacing={1} alignItems="center" sx={{ mb: 4, mt: 2 }}>
          <Typography variant="h4" sx={{ fontWeight: 800, letterSpacing: "-0.5px", textAlign: "center" }}>
            Help & FAQ
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ textAlign: "center", maxWidth: "600px" }}>
            Everything you need to know about using ETS Bot. Watch the intro video below, then browse the frequently asked questions.
          </Typography>
        </Stack>

        {/* Intro Video Placeholder */}
        <Box
          sx={{
            width: "100%",
            aspectRatio: "16 / 9",
            backgroundColor: "#000",
            borderRadius: 3,
            mb: 5,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            overflow: "hidden",
            position: "relative",
          }}
        >
          <video
            controls
            width="100%"
            height="100%"
            poster=""
            style={{ backgroundColor: "#000" }}
          >
            <source src="/intro-video-placeholder.mp4" type="video/mp4" />
            Your browser does not support the video element.
          </video>
          {/* Overlay for when video file doesn't exist */}
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "rgba(0,0,0,0.85)",
              color: "white",
              pointerEvents: "none",
            }}
          >
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: "50%",
                border: "3px solid rgba(255,255,255,0.8)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mb: 2,
              }}
            >
              <Box
                sx={{
                  width: 0,
                  height: 0,
                  borderTop: "18px solid transparent",
                  borderBottom: "18px solid transparent",
                  borderLeft: "30px solid rgba(255,255,255,0.8)",
                  ml: "6px",
                }}
              />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
              Intro Video Coming Soon
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.7 }}>
              A walkthrough of ETS Bot will be available here
            </Typography>
          </Box>
        </Box>

        {/* Quick Reference: Button Guide */}
        <Box sx={{ mb: 5 }}>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 2.5, letterSpacing: "-0.3px" }}>
            Quick Reference — Button Guide
          </Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
              gap: 2,
            }}
          >
            {[
              { icon: <FilterAltOutlinedIcon color="action" />, label: "Filter (funnel)", desc: "Select episodes to narrow down answers" },
              { icon: <AddIcon color="action" />, label: "+ Button", desc: "Open quick chat settings (source scope, memory)" },
              { icon: <SendIcon color="primary" />, label: "Send", desc: "Submit your question to the chatbot" },
              { icon: <ThumbUpOutlinedIcon color="action" />, label: "Thumbs Up", desc: "Rate a response as helpful" },
              { icon: <ThumbDownOutlinedIcon color="action" />, label: "Thumbs Down", desc: "Rate a response as unhelpful" },
              { icon: <ContentCopyIcon color="action" />, label: "Copy", desc: "Copy the bot's response to your clipboard" },
            ].map((item, idx) => (
              <Box
                key={idx}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  p: 2,
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: "divider",
                  backgroundColor: isDark ? "background.paper" : "#fafafa",
                }}
              >
                <Box sx={{ flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", width: 40, height: 40, borderRadius: "50%", backgroundColor: isDark ? "action.selected" : "#f0f0f0" }}>
                  {item.icon}
                </Box>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{item.label}</Typography>
                  <Typography variant="caption" color="text.secondary">{item.desc}</Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>

        <Divider sx={{ mb: 4 }} />

        {/* FAQ Section */}
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 3, letterSpacing: "-0.3px" }}>
          Frequently Asked Questions
        </Typography>

        {categories.map((category) => (
          <Box key={category} sx={{ mb: 4 }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
              <Chip
                icon={categoryIcons[category] as React.ReactElement}
                label={category}
                size="small"
                sx={{
                  backgroundColor: categoryColors[category] || "#f5f5f5",
                  fontWeight: 600,
                  "& .MuiChip-icon": { color: "text.secondary" },
                }}
              />
            </Stack>
            {faqItems
              .filter((item) => item.category === category)
              .map((item, idx) => {
                const panelId = item.anchor ?? `${category}-${idx}`;
                return (
                  <Accordion
                    key={panelId}
                    id={panelId}
                    expanded={expandedPanel === panelId}
                    onChange={handleAccordionChange(panelId)}
                    disableGutters
                    elevation={0}
                    sx={{
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: "8px !important",
                      mb: 1.5,
                      "&:before": { display: "none" },
                      "&.Mui-expanded": {
                        borderColor: "primary.light",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                      },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      sx={{
                        px: 2.5,
                        py: 0.5,
                        "& .MuiAccordionSummary-content": { my: 1.5 },
                      }}
                    >
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        {item.question}
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ px: 2.5, pb: 2.5, pt: 0 }}>
                      {item.answer}
                    </AccordionDetails>
                  </Accordion>
                );
              })}
          </Box>
        ))}

        {/* Still have questions? */}
        <Box
          sx={{
            mt: 4,
            p: 3,
            borderRadius: 3,
            backgroundColor: isDark ? "background.paper" : "#f0f8ff",
            border: "1px solid",
            borderColor: isDark ? "divider" : "#bbdefb",
            textAlign: "center",
          }}
        >
          <InfoOutlinedIcon sx={{ fontSize: 32, color: "primary.main", mb: 1 }} />
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
            Still have questions?
          </Typography>
          <Typography variant="body2" color="text.secondary">
            If something isn't covered here, feel free to ask the chatbot itself — it can help guide you on how to use it! Otherwise, reach out to your administrator.
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

export default FaqPage;
